import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import ReactSelect, { GroupBase } from 'react-select';
import { toast } from 'react-toastify';

import {
  Breadcrumbs,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
} from '@mui/material';
import { BLANK_PDF, Template } from '@pdfme/common';
import { Designer } from '@pdfme/ui';
import { Form, Formik } from 'formik';
import { v4 as uuidv4 } from 'uuid';
import * as Yup from 'yup';

import { paths, routes } from 'src/app/routes';
import Page from 'src/layouts/BaseLayout/components/Page/Page';
import Card from 'src/shared/components/Card/Card';
import Icon from 'src/shared/components/Icon/Icon';
import { IconType } from 'src/shared/components/Icon/IconType';
import Input from 'src/shared/components/Input/Input';
import PicturePicker from 'src/shared/components/PicturePicker/PicturePicker';
import Select from 'src/shared/components/Select/Select';
import Spinner from 'src/shared/components/Spinner/Spinner';
import DEFAULT_TEMPLATE from 'src/shared/components/TemplateDesigner/default-template.json';
import { DESIGNER_PLUGINS } from 'src/shared/components/TemplateDesigner/designerPlugins';
import {
  toDesignerTemplate,
  toPersistedTemplate,
} from 'src/shared/components/TemplateDesigner/templateMapping';
import {
  cloneDeep,
  downloadJsonFile,
  generatePDF,
  handleLoadTemplate,
  readFile,
} from 'src/shared/components/TemplateDesigner/utils';
import { ErrorResponse } from 'src/shared/models';

import { COMPANIES } from 'src/features/companies/slices/companiesSlice';
import { SupportTypeEnum } from 'src/features/credentials/models';
import {
  useCreateDocumentTemplateMutation,
  useLazyGetDocumentTemplateQuery,
} from 'src/features/documents/api/documentsApi';
import { useCheckAuthorization } from 'src/features/hooks';
import { CONTAINER_ID_ACTION } from 'src/features/notifications/components/NotificationContainer/NotificationContainer';
import { State } from 'src/features/store/store';
import {
  useCreateTemplateForCompanyMutation,
  useCreateTemplateMutation,
  useGetTemplatesQuery,
  useLazyGetTemplatesByCompanyQuery,
} from 'src/features/templates/api/templatesApi';
import {
  CreateCredentialTemplateRequest,
  Template as CredentialTemplate,
} from 'src/features/templates/models';

import { useLoadFonts } from './hooks';
import { SettingsContext } from '../Settings/SettingsContext';

import './CreateTemplate.scss';

const modelMap = {
  model1: () => import('./templates/model1.json'),
  model2: () => import('./templates/model2.json'),
  model3: () => import('./templates/model3.json'),
};

type ModelKey = keyof typeof modelMap;

interface FormValues {
  templateName: string;
  image: string;
  support?: string;
}

interface StartFromOption {
  value: string;
  label: string;
}

const initialValues: FormValues = {
  templateName: '',
  image: '',
};

const createTemplateValidation = Yup.object().shape({
  templateName: Yup.string().required('This field is required'),
});

const MODEL_OPTIONS: StartFromOption[] = [
  { label: 'Certificate Model 1', value: 'model1' },
  { label: 'Certificate Model 2', value: 'model2' },
  { label: 'Certificate Model 3', value: 'model3' },
];

const CUSTOM_PDF_OPTION: StartFromOption = {
  label: 'Custom PDF',
  value: 'other',
};

const TEMPLATE_TYPES = ['Certificate', 'Membership', 'D-SRB'];

const FILE_SIZE = 1048576; // 1MB

const CreateTemplate = (): JSX.Element => {
  const designerRef = useRef<HTMLDivElement | null>(null);
  const designer = useRef<Designer | null>(null);
  const initializedStartFrom = useRef(false);

  const [searchParams] = useSearchParams();
  const fromUid = searchParams.get('from');
  const companyParam = searchParams.get('company');

  const [startFrom, setStartFrom] = useState<StartFromOption | null>(
    MODEL_OPTIONS[0]
  );
  const [designerSource, setDesignerSource] = useState<Template>(
    DEFAULT_TEMPLATE as Template
  );
  const [cloneSource, setCloneSource] = useState<CredentialTemplate | null>(
    null
  );
  const [isDesignLoading, setIsDesignLoading] = useState(false);
  const [credentialType, setCredentialType] = useState<string>(
    TEMPLATE_TYPES[0].toUpperCase()
  );

  const currentCompany = useSelector((s: State) => s[COMPANIES].company);
  const settingsContext = useContext(SettingsContext);
  const navigate = useNavigate();

  const isCompanyTemplate =
    Boolean(companyParam) || settingsContext?.prevPage === 'Company';
  const companyUid = companyParam ?? currentCompany?.uid;
  const backPath =
    isCompanyTemplate && companyUid
      ? (settingsContext?.getPath(companyUid, routes.companies.templates) ?? '')
      : paths.settingsTemplates;

  const { data: globalTemplates } = useGetTemplatesQuery(undefined, {
    skip: Boolean(companyUid),
  });
  const [fetchCompanyTemplates, companyTemplatesResult] =
    useLazyGetTemplatesByCompanyQuery();
  const [getDocumentTemplate] = useLazyGetDocumentTemplateQuery();

  useEffect(() => {
    if (companyUid) {
      fetchCompanyTemplates(companyUid);
    }
  }, [companyUid, fetchCompanyTemplates]);

  const credentialTemplates = useMemo(() => {
    if (companyUid) {
      return companyTemplatesResult.data?.data ?? [];
    }
    return globalTemplates?.data ?? [];
  }, [companyUid, companyTemplatesResult.data, globalTemplates?.data]);

  const startFromOptions = useMemo<GroupBase<StartFromOption>[]>(() => {
    const existing = credentialTemplates
      .filter((template) => template.designTemplateUid)
      .map((template) => ({
        value: `template:${template.uid}`,
        label: template.name,
      }));

    return [
      { label: 'Models', options: MODEL_OPTIONS },
      ...(existing.length
        ? [{ label: 'Existing templates', options: existing }]
        : []),
      { label: 'Other', options: [CUSTOM_PDF_OPTION] },
    ];
  }, [credentialTemplates]);

  const [createTemplate, createTemplateResult] =
    useCreateDocumentTemplateMutation();
  const [createCredentialTemplate, createCredentialTemplateResult] =
    useCreateTemplateMutation();
  const [createTemplateForCompany, createTemplateForCompanyResult] =
    useCreateTemplateForCompanyMutation();

  useCheckAuthorization();

  const getSupportValues = () => {
    return Object.keys(SupportTypeEnum).map((key) => {
      return {
        label: key,
        value: key,
      };
    });
  };

  const initialTemplateValues = useMemo(() => {
    const base = isCompanyTemplate
      ? { ...initialValues, support: '' }
      : initialValues;
    if (cloneSource) {
      return {
        ...base,
        templateName: `${cloneSource.name} (copy)`,
        image: cloneSource.image,
      };
    }
    return base;
  }, [cloneSource, isCompanyTemplate]);

  const validationSchema = isCompanyTemplate
    ? createTemplateValidation.concat(
        Yup.object().shape({
          support: Yup.string().required('This field is required'),
        })
      )
    : createTemplateValidation;

  const fonts = useLoadFonts();
  const designerSourceRef = useRef(designerSource);
  designerSourceRef.current = designerSource;

  useEffect(() => {
    if (!designerRef.current) {
      return undefined;
    }

    const instance = new Designer({
      domContainer: designerRef.current,
      template: designerSourceRef.current,
      plugins: DESIGNER_PLUGINS,
      options: {
        font: fonts,
      },
    });
    designer.current = instance;

    return () => {
      instance.destroy();
      designer.current = null;
    };
  }, [fonts]);

  useEffect(() => {
    if (designer.current) {
      designer.current.updateTemplate(cloneDeep(designerSource));
    }
  }, [designerSource]);

  const applyModel = useCallback(async (modelKey: ModelKey) => {
    const modelTemplate = (await modelMap[modelKey]()) as Template;
    setCloneSource(null);
    setDesignerSource(modelTemplate);
    setStartFrom(
      MODEL_OPTIONS.find((option) => option.value === modelKey) ??
        MODEL_OPTIONS[0]
    );
  }, []);

  const loadExistingTemplate = useCallback(
    async (credentialUid: string) => {
      const source = credentialTemplates.find(
        (template) => template.uid === credentialUid
      );

      if (!source?.designTemplateUid) {
        toast.error('Could not load the selected template', {
          containerId: CONTAINER_ID_ACTION,
        });
        await applyModel('model1');
        return;
      }

      setIsDesignLoading(true);
      try {
        const design = await getDocumentTemplate(
          source.designTemplateUid
        ).unwrap();
        const designerTemplate = toDesignerTemplate(
          design,
          source.renderParams ?? {}
        );
        setCloneSource(source);
        setDesignerSource(designerTemplate);
        setStartFrom({
          value: `template:${source.uid}`,
          label: source.name,
        });
        if (source.type) {
          setCredentialType(source.type);
        }
      } catch {
        toast.error('Could not load the template design', {
          containerId: CONTAINER_ID_ACTION,
        });
        await applyModel('model1');
      } finally {
        setIsDesignLoading(false);
      }
    },
    [applyModel, credentialTemplates, getDocumentTemplate]
  );

  useEffect(() => {
    if (initializedStartFrom.current) {
      return;
    }

    if (fromUid) {
      if (credentialTemplates.length === 0) {
        return;
      }
      initializedStartFrom.current = true;
      void loadExistingTemplate(fromUid);
      return;
    }

    initializedStartFrom.current = true;
    void applyModel('model1');
  }, [applyModel, credentialTemplates.length, fromUid, loadExistingTemplate]);

  const onTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCredentialType((event.target as HTMLInputElement).value);
  };

  const onChangeStartFrom = (option: StartFromOption | null) => {
    if (!option) {
      return;
    }

    setStartFrom(option);

    if (option.value === 'other') {
      setCloneSource(null);
      const basePdf = designer.current
        ? designer.current.getTemplate().basePdf
        : DEFAULT_TEMPLATE.basePdf;
      setDesignerSource({
        ...(DEFAULT_TEMPLATE as Template),
        basePdf,
      });
      return;
    }

    if (option.value.startsWith('template:')) {
      const credentialUid = option.value.replace('template:', '');
      void loadExistingTemplate(credentialUid);
      return;
    }

    if (option.value in modelMap) {
      void applyModel(option.value as ModelKey);
    }
  };

  const onUploadBasePDF = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target && e.target.files) {
      readFile(e.target.files[0], 'dataURL').then(async (basePdf) => {
        if (designer.current) {
          designer.current.updateTemplate(
            Object.assign(cloneDeep(designer.current.getTemplate()), {
              basePdf,
            })
          );
        }
      });
    }
  };

  const onDownloadTemplate = () => {
    if (designer.current) {
      downloadJsonFile(designer.current.getTemplate(), 'template-design');
    }
  };

  const onPreviewPDF = () => {
    generatePDF(designer.current);
  };

  const onClearTemplate = () => {
    if (designer.current) {
      designer.current.updateTemplate(cloneDeep(designerSource));
    }
  };

  const onSubmit = async (values: FormValues, resetForm: () => void) => {
    if (designer.current) {
      const currentTemplate = designer.current.getTemplate();
      const { schemas, renderParams } = toPersistedTemplate(currentTemplate);

      const templateId = uuidv4();

      const templateData = {
        ...values,
        templateUid: templateId,
        schemas: [schemas],
        basePdf:
          (designer.current.getTemplate().basePdf as string) ?? BLANK_PDF,
        templateType: 'STATIC',
      };

      const requestObject: CreateCredentialTemplateRequest = {
        name: values.templateName,
        image: values.image,
        description: '',
        designTemplateUid: templateId,
        type: credentialType,
      };

      if (Object.keys(renderParams).length > 0) {
        requestObject.renderParams = renderParams;
      }

      createTemplate(templateData)
        .then(() => {
          if (isCompanyTemplate && companyUid) {
            createTemplateForCompany({
              ...requestObject,
              companyUid,
            })
              .then(() => {
                navigate(backPath);
              })
              .catch((error) => console.log(error));
          } else {
            createCredentialTemplate(requestObject)
              .then(() => {
                resetForm();
              })
              .catch((error) => console.log(error));
          }
        })
        .catch((error) => console.log(error));
    }
  };

  useEffect(() => {
    if (
      createTemplateResult.isSuccess &&
      (createCredentialTemplateResult.isSuccess ||
        createTemplateForCompanyResult.isSuccess)
    ) {
      toast.success('Created successfully', {
        containerId: CONTAINER_ID_ACTION,
      });
    }
  }, [
    createCredentialTemplateResult.isSuccess,
    createTemplateForCompanyResult.isSuccess,
    createTemplateResult.isSuccess,
  ]);

  useEffect(() => {
    if (createTemplateResult.isError) {
      toast.error(
        `Error: ${(createTemplateResult.error as ErrorResponse).data.message}`,
        {
          containerId: CONTAINER_ID_ACTION,
        }
      );
    }
  }, [createTemplateResult.error, createTemplateResult.isError]);

  useEffect(() => {
    if (createCredentialTemplateResult.isError) {
      toast.error(
        `Error: ${
          (createCredentialTemplateResult.error as ErrorResponse).data.message
        }`,
        {
          containerId: CONTAINER_ID_ACTION,
        }
      );
    }
  }, [
    createCredentialTemplateResult.error,
    createCredentialTemplateResult.isError,
  ]);

  return (
    <Page>
      <div className="create-template">
        <div className="create-template__header">
          <Breadcrumbs>
            <Link
              className="create-template-header__bread-crumbs"
              to={backPath}
            >
              {settingsContext?.prevPage ?? 'Templates'}
            </Link>
            <h1>
              {cloneSource
                ? `Create Template from ${cloneSource.name}`
                : 'Create New Template'}
            </h1>
          </Breadcrumbs>
          <div className="create-template-header__buttons-block">
            <label className="template-designer__label-button">
              Upload Existing Design
              <input
                type="file"
                accept="application/json"
                onChange={(e) => handleLoadTemplate(e, designer.current)}
              />
            </label>
            <Button
              variant="outlined"
              color="primary"
              onClick={onDownloadTemplate}
            >
              Download Current Design
            </Button>
          </div>
        </div>
        <div className="create-template__content">
          <Card className="create-template__card">
            <h2 className="create-template__form-title">Template Editor</h2>
            <Formik
              initialValues={initialTemplateValues}
              enableReinitialize
              validationSchema={validationSchema}
              onSubmit={(values, { resetForm }) => onSubmit(values, resetForm)}
            >
              <Form className="template-designer__header">
                <div className="template-designer__fields-block">
                  <PicturePicker
                    name="image"
                    className="create-template-form__picture"
                    size={FILE_SIZE}
                  />
                  <FormControl>
                    <FormLabel>Credential Type</FormLabel>
                    <RadioGroup
                      value={credentialType}
                      name="credentialType"
                      row
                      onChange={onTypeChange}
                    >
                      {TEMPLATE_TYPES?.map((type, key) => (
                        <FormControlLabel
                          key={key}
                          value={type.toUpperCase()}
                          control={<Radio />}
                          label={type}
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>
                  <Input name="templateName" label="Template name" required />
                  <div>
                    <label className="input__label" htmlFor="startFrom">
                      Start from
                    </label>
                    <ReactSelect<
                      StartFromOption,
                      false,
                      GroupBase<StartFromOption>
                    >
                      inputId="startFrom"
                      options={startFromOptions}
                      value={startFrom}
                      onChange={onChangeStartFrom}
                      theme={(theme) => ({
                        ...theme,
                        colors: {
                          ...theme.colors,
                          primary: 'var(--ncap-300)',
                        },
                      })}
                    />
                  </div>
                  {startFrom?.value === 'other' && (
                    <label className="template-designer__label-button">
                      Upload BasePDF
                      <input
                        type="file"
                        accept="application/pdf"
                        onChange={onUploadBasePDF}
                      />
                    </label>
                  )}
                  {isCompanyTemplate ? (
                    <Select
                      name="support"
                      label="Support"
                      options={getSupportValues()}
                      required
                    />
                  ) : null}
                </div>
                <div className="template-designer__buttons-block">
                  <div className="template-designer__buttons-block_first">
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={onPreviewPDF}
                      startIcon={<Icon icon={IconType.Document} />}
                    >
                      Preview PDF
                    </Button>
                    {createCredentialTemplateResult.isLoading ||
                    createTemplateForCompanyResult.isLoading ||
                    createTemplateResult.isLoading ? (
                      <Spinner />
                    ) : (
                      <Button
                        variant="contained"
                        startIcon={<Icon icon={IconType.Plus} />}
                        type="submit"
                      >
                        Create
                      </Button>
                    )}
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={
                        <Icon
                          icon={IconType.Cross}
                          className="template-designer__button-icon_discard"
                        />
                      }
                      onClick={onClearTemplate}
                    >
                      Clear
                    </Button>
                  </div>
                </div>
              </Form>
            </Formik>
          </Card>
          <div className="create-template-designer-wrapper">
            {isDesignLoading ? (
              <div className="create-template-designer__loading">
                <Spinner />
              </div>
            ) : null}
            <div className="create-template-designer" ref={designerRef}></div>
          </div>
        </div>
      </div>
    </Page>
  );
};

export default CreateTemplate;
